import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { BarChart3, Bell, Building2, CalendarDays, CheckCircle2, CreditCard, Eye, FileText, LayoutDashboard, LogOut, Plus, Printer, Search, Settings, Sparkles, Trash2, Wallet, X, XCircle } from 'lucide-react';
import './ControlCenterAgreements.css';
const API_BASE_URL = window.location.origin;
const getToken = () => localStorage.getItem('vakra_control_admin_token') || sessionStorage.getItem('vakra_control_admin_token') || '';
const getToday = () => new Date().toISOString().slice(0, 10);
const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const safeJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};
const apiRequest = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};
const StatusBadge = ({ value, type = 'status' }) => {
  const status = String(value || 'pending').toLowerCase();
  return <span className={type === 'payment' ? `vcca-payment-status ${status}` : `vcca-status ${status}`}>{status.replaceAll('_', ' ')}</span>;
};
const ControlCenterAgreements = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  let savedAdmin = null;
  try {
    const rawAdmin = localStorage.getItem('vakra_control_admin') || sessionStorage.getItem('vakra_control_admin');
    if (rawAdmin) savedAdmin = JSON.parse(rawAdmin);
  } catch (error) {
    console.error('Saved admin parse error:', error);
  }
  const admin = outletContext?.admin || savedAdmin || null;
  const [agreements, setAgreements] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [detailsAgreement, setDetailsAgreement] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [form, setForm] = useState({
    subscriptionId: '',
    orderId: '',
    agreementDate: getToday(),
    agreementVersion: '1.0',
    terms: '',
    notes: '',
  });
  const resetForm = () => {
    setForm({
      subscriptionId: '',
      orderId: '',
      agreementDate: getToday(),
      agreementVersion: '1.0',
      terms: '',
      notes: '',
    });
  };
  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [agreementData, subscriptionData, paymentData] = await Promise.all([
        apiRequest(`${API_BASE_URL}/api/control-center/agreements`),
        apiRequest(`${API_BASE_URL}/api/control-center/subscriptions`),
        apiRequest(`${API_BASE_URL}/api/control-center/payments`),
      ]);
      setAgreements(Array.isArray(agreementData.agreements) ? agreementData.agreements : []);
      setSubscriptions(Array.isArray(subscriptionData.subscriptions) ? subscriptionData.subscriptions : []);
      setPayments(Array.isArray(paymentData.payments) ? paymentData.payments : []);
    } catch (loadError) {
      console.error('Agreement load error:', loadError);
      setError(loadError.message || 'Agreement data load nahi ho paya.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, []);
  const selectedSubscription = useMemo(() => subscriptions.find((item) => String(item.subscriptionId) === String(form.subscriptionId)), [subscriptions, form.subscriptionId]);
  const matchingPayments = useMemo(() => {
    if (!selectedSubscription) return [];
    return payments.filter((payment) => String(payment.subscriptionId) === String(selectedSubscription.subscriptionId));
  }, [payments, selectedSubscription]);
  const stats = useMemo(() => ({
    total: agreements.length,
    pending: agreements.filter((item) => item.status === 'pending_acceptance').length,
    accepted: agreements.filter((item) => item.status === 'accepted').length,
    active: agreements.filter((item) => item.status === 'active').length,
    expired: agreements.filter((item) => item.status === 'expired').length,
    cancelled: agreements.filter((item) => item.status === 'cancelled').length,
  }), [agreements]);
  const filteredAgreements = useMemo(() => {
    const query = search.trim().toLowerCase();
    return agreements.filter((agreement) => {
      const snapshot = safeJson(agreement.agreementSnapshot);
      const purchaseType = snapshot?.purchase?.type || '';
      const orderText = agreement.orderId ? String(agreement.orderId) : '';
      const matchesSearch = !query || String(agreement.businessName || '').toLowerCase().includes(query) || String(agreement.ownerName || '').toLowerCase().includes(query) || String(agreement.mobile || '').toLowerCase().includes(query) || String(agreement.agreementNo || '').toLowerCase().includes(query) || String(agreement.planName || '').toLowerCase().includes(query) || orderText.includes(query) || String(purchaseType).toLowerCase().includes(query);
      const matchesStatus = statusFilter === 'all' || agreement.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [agreements, search, statusFilter]);
  const openAgreementDetails = async (agreement) => {
    try {
      setError('');
      const data = await apiRequest(`${API_BASE_URL}/api/control-center/agreements/${agreement.agreementId}`);
      setDetailsAgreement(data.agreement || agreement);
    } catch (viewError) {
      setError(viewError.message || 'Agreement details load nahi ho payi.');
    }
  };
  const printAgreement = (agreement) => {
    if (!agreement) return;
    const agreementSnapshot = safeJson(agreement.agreementSnapshot);
    const purchase = agreementSnapshot?.purchase || null;
    const vendor = agreementSnapshot?.vendor || {};
    const items = Array.isArray(purchase?.items) ? purchase.items : [];
    const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));
    const itemRows = items.map((item) => {
      const warranty = item.warranty || {};
      const warrantyText = warranty.enabled ? `${warranty.months || ''} Months ${warranty.type || 'Warranty'}`.trim() : '—';
      return `<tr><td>${escapeHtml(item.itemName || item.name || 'Item')}</td><td>${escapeHtml(item.itemType || '')}</td><td>${escapeHtml(item.quantity || 1)}</td><td>₹${Number(item.unitPrice || 0).toLocaleString('en-IN')}</td><td>₹${Number(item.lineTotal || 0).toLocaleString('en-IN')}</td><td>${escapeHtml(warrantyText)}</td></tr>`;
    }).join('');
    const printWindow = window.open('', '_blank', 'width=980,height=800');
    if (!printWindow) {
      setError('Print window block ho gaya. Browser me pop-up allow karein.');
      return;
    }
    printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(agreement.agreementNo || 'VAKRA Agreement')}</title><style>body{font-family:Arial,sans-serif;color:#111827;margin:0;padding:28px}.page{max-width:900px;margin:auto}.head{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #111827;padding-bottom:16px}.brand h1{margin:0;font-size:28px}.brand p,.meta p{margin:4px 0;color:#4b5563;font-size:13px}.section{margin-top:20px}.section h3{font-size:15px;margin:0 0 10px;border-bottom:1px solid #d1d5db;padding-bottom:6px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px}.row{font-size:13px}.row b{display:block;margin-bottom:2px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #d1d5db;padding:7px;text-align:left}th{background:#f3f4f6}.terms{white-space:pre-wrap;font-size:12px;line-height:1.55}.signature{display:grid;grid-template-columns:1fr 1fr;gap:70px;margin-top:50px}.signline{border-top:1px solid #111;padding-top:6px;font-size:12px}@media print{body{padding:0}.page{max-width:none}}</style></head><body><div class="page"><div class="head"><div class="brand"><h1>VAKRA</h1><p>Service & Subscription Agreement</p></div><div class="meta"><p><b>Agreement No:</b> ${escapeHtml(agreement.agreementNo || '—')}</p><p><b>Version:</b> ${escapeHtml(agreement.agreementVersion || '1.0')}</p><p><b>Date:</b> ${escapeHtml(agreement.agreementDate || '—')}</p></div></div><div class="section"><h3>Vendor Details</h3><div class="grid"><div class="row"><b>Business Name</b>${escapeHtml(agreement.businessName || vendor.businessName || '—')}</div><div class="row"><b>Owner Name</b>${escapeHtml(agreement.ownerName || vendor.ownerName || '—')}</div><div class="row"><b>Mobile</b>${escapeHtml(agreement.mobile || vendor.mobile || '—')}</div><div class="row"><b>Email</b>${escapeHtml(agreement.email || vendor.email || '—')}</div><div class="row" style="grid-column:1/-1"><b>Address</b>${escapeHtml(agreement.address || vendor.address || '—')}</div></div></div><div class="section"><h3>Subscription / Purchase</h3><div class="grid"><div class="row"><b>Plan</b>${escapeHtml(agreement.planName || agreementSnapshot?.subscription?.planName || '—')}</div><div class="row"><b>Order ID</b>${escapeHtml(agreement.orderId || purchase?.id || '—')}</div><div class="row"><b>Start Date</b>${escapeHtml(agreement.startDate || '—')}</div><div class="row"><b>Expiry Date</b>${escapeHtml(agreement.expiryDate || '—')}</div><div class="row"><b>Final Amount</b>₹${Number(purchase?.finalAmount ?? agreement.amount ?? 0).toLocaleString('en-IN')}</div><div class="row"><b>Payment Status</b>${escapeHtml(agreement.paymentStatus || 'pending')}</div></div></div>${items.length ? `<div class="section"><h3>Items</h3><table><thead><tr><th>Item</th><th>Type</th><th>Qty</th><th>Unit Price</th><th>Total</th><th>Warranty</th></tr></thead><tbody>${itemRows}</tbody></table></div>` : ''}<div class="section"><h3>Terms & Conditions</h3><div class="terms">${escapeHtml(agreement.terms || '')}</div></div><div class="signature"><div class="signline">Vendor Signature / Name</div><div class="signline">For VAKRA</div></div></div><script>window.onload=()=>{window.print();};<\/script></body></html>`);
    printWindow.document.close();
  };
  const deletePendingAgreement = async (agreement) => {
    if (!agreement?.agreementId) return;
    const status = String(agreement.status || '').toLowerCase();
    if (!['draft', 'pending_acceptance'].includes(status)) {
      setError('Sirf Draft ya Pending Acceptance agreement delete kiya ja sakta hai.');
      return;
    }
    const confirmed = window.confirm(`Delete pending agreement ${agreement.agreementNo || `#${agreement.agreementId}`}?\n\nVendor: ${agreement.businessName || 'Vendor'}\n\nAccepted ya paid agreement delete nahi hoga.`);
    if (!confirmed) return;
    try {
      setError('');
      setSuccessMessage('');
      await apiRequest(`${API_BASE_URL}/api/control-center/agreements/${agreement.agreementId}`, { method: 'DELETE' });
      setSuccessMessage('Pending agreement delete ho gaya.');
      if (detailsAgreement?.agreementId === agreement.agreementId) setDetailsAgreement(null);
      await loadData();
    } catch (deleteError) {
      setError(deleteError.message || 'Pending agreement delete nahi ho paya.');
    }
  };
  const handleCreateAgreement = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!form.subscriptionId) {
      setError('Subscription select karein.');
      return;
    }
    if (!form.agreementDate) {
      setError('Agreement date select karein.');
      return;
    }
    const orderId = String(form.orderId || '').trim();
    if (orderId && (!Number.isInteger(Number(orderId)) || Number(orderId) <= 0)) {
      setError('Purchase Order ID valid hona chahiye.');
      return;
    }
    try {
      setSaving(true);
      const data = await apiRequest(`${API_BASE_URL}/api/control-center/agreements`, {
        method: 'POST',
        body: JSON.stringify({
          subscriptionId: Number(form.subscriptionId),
          orderId: orderId ? Number(orderId) : null,
          agreementDate: form.agreementDate,
          agreementVersion: String(form.agreementVersion || '1.0').trim(),
          terms: form.terms.trim(),
          notes: form.notes.trim(),
        }),
      });
      setShowModal(false);
      resetForm();
      setSuccessMessage(data.agreement?.agreementNo ? `Agreement created: ${data.agreement.agreementNo}` : 'Agreement successfully created.');
      await loadData();
      if (data.agreement?.agreementId) {
        try {
          const detailData = await apiRequest(`${API_BASE_URL}/api/control-center/agreements/${data.agreement.agreementId}`);
          setDetailsAgreement(detailData.agreement || data.agreement);
        } catch (detailError) {
          console.error('Created agreement detail load error:', detailError);
        }
      }
    } catch (saveError) {
      setError(saveError.message || 'Agreement create nahi ho paya.');
    } finally {
      setSaving(false);
    }
  };
  const handleLogout = () => {
    localStorage.removeItem('vakra_control_admin_token');
    localStorage.removeItem('vakra_control_admin');
    sessionStorage.removeItem('vakra_control_admin_token');
    sessionStorage.removeItem('vakra_control_admin');
    navigate('/control-center/login', { replace: true });
  };
  const menuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/control-center/dashboard' },
    { label: 'Vendors', icon: Building2, path: '/control-center/vendors' },
    { label: 'Subscriptions', icon: CreditCard, path: '/control-center/subscriptions' },
    { label: 'Payments', icon: Wallet, path: '/control-center/payments' },
    { label: 'Agreements', icon: FileText, path: '/control-center/agreements', active: true },
    { label: 'Smart Offer Digest', icon: Sparkles, path: '/control-center/smart-digest' },
    { label: 'Analytics', icon: BarChart3, path: '/control-center/analytics' },
    { label: 'Settings', icon: Settings, path: '/control-center/settings' },
  ];
  const handleMenuClick = (item) => {
    if (['/control-center/dashboard', '/control-center/vendors', '/control-center/subscriptions', '/control-center/payments', '/control-center/agreements'].includes(item.path)) {
      navigate(item.path);
      return;
    }
    alert(`${item.label} page next phase me banega.`);
  };
  const snapshot = safeJson(detailsAgreement?.agreementSnapshot);
  const snapshotPurchase = snapshot?.purchase || null;
  const snapshotItems = Array.isArray(snapshotPurchase?.items) ? snapshotPurchase.items : [
    ...(Array.isArray(snapshotPurchase?.hardware) ? snapshotPurchase.hardware.map((item) => ({ ...item, itemType: 'hardware' })) : []),
    ...(Array.isArray(snapshotPurchase?.extras) ? snapshotPurchase.extras.map((item) => ({ ...item, itemType: 'extra' })) : []),
  ];
  return (
    <div className="vcca-app">
      <aside className="vcca-sidebar">
        <div className="vcca-brand"><div className="vcca-logo">V</div><div><strong>VAKRA</strong><span>CONTROL CENTER</span></div></div>
        <div className="vcca-admin"><div className="vcca-avatar">{String(admin?.name || 'A').charAt(0).toUpperCase()}</div><div><strong>{admin?.name || 'Administrator'}</strong><span>{admin?.role || 'admin'}</span></div></div>
        <nav>{menuItems.map((item) => { const Icon = item.icon; return <button type="button" key={item.label} className={item.active ? 'active' : ''} onClick={() => handleMenuClick(item)}><Icon size={18} />{item.label}</button>; })}</nav>
        <button type="button" className="vcca-logout" onClick={handleLogout}><LogOut size={18} />Logout</button>
      </aside>
      <main className="vcca-main">
        <header className="vcca-topbar"><div><span>VAKRA CONTROL CENTER</span><h1>Agreements</h1></div><button type="button" className="vcca-bell"><Bell size={20} /></button></header>
        <div className="vcca-content">
          <section className="vcca-heading">
            <div><span><FileText size={16} /> Agreement Management</span><h2>Vendor Agreements</h2><p>Subscription, purchase order, hardware warranty, payment aur agreement acceptance ko ek jagah manage karein.</p></div>
            <button type="button" onClick={() => { setError(''); setSuccessMessage(''); resetForm(); setShowModal(true); }}><Plus size={18} />Create Agreement</button>
          </section>
          {successMessage && <div className="vcca-success">{successMessage}</div>}
          <section className="vcca-stats">
            <article><FileText /><span>Total Agreements</span><strong>{stats.total}</strong></article>
            <article><CalendarDays /><span>Pending Acceptance</span><strong>{stats.pending}</strong></article>
            <article><CheckCircle2 /><span>Accepted</span><strong>{stats.accepted}</strong></article>
            <article><CheckCircle2 /><span>Active</span><strong>{stats.active}</strong></article>
            <article><CalendarDays /><span>Expired</span><strong>{stats.expired}</strong></article>
            <article><XCircle /><span>Cancelled</span><strong>{stats.cancelled}</strong></article>
          </section>
          <section className="vcca-panel">
            <div className="vcca-toolbar">
              <div className="vcca-search"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search vendor, agreement, plan or order ID" /></div>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All Status</option>
                <option value="pending_acceptance">Pending Acceptance</option>
                <option value="accepted">Accepted</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div className="vcca-table-heading"><div><h3>Agreement Directory</h3><p>{filteredAgreements.length} agreement{filteredAgreements.length === 1 ? '' : 's'}</p></div></div>
            {error && !showModal && !detailsAgreement && <div className="vcca-error">{error}</div>}
            {loading ? <div className="vcca-empty">Loading agreements...</div> : filteredAgreements.length === 0 ? <div className="vcca-empty"><FileText size={34} /><strong>No agreements found</strong><span>Create Agreement button se pehla agreement banayein.</span></div> : (
              <div className="vcca-table-wrap">
                <table>
                  <thead><tr><th>Agreement</th><th>Vendor</th><th>Plan / Purchase</th><th>Amount</th><th>Order</th><th>Payment</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredAgreements.map((agreement) => {
                      const itemSnapshot = safeJson(agreement.agreementSnapshot);
                      const purchaseType = itemSnapshot?.purchase?.type || (agreement.orderId ? 'purchase' : 'subscription');
                      return (
                        <tr key={agreement.agreementId}>
                          <td><strong>{agreement.agreementNo}</strong><small>{agreement.agreementDate} · v{agreement.agreementVersion || '1.0'}</small></td>
                          <td><strong>{agreement.businessName}</strong><small>{agreement.mobile || '—'}</small></td>
                          <td><strong>{agreement.planName || '—'}</strong><small>{String(purchaseType).replaceAll('_', ' ')}</small></td>
                          <td className="vcca-amount">{money(itemSnapshot?.purchase?.finalAmount ?? agreement.amount)}</td>
                          <td>{agreement.orderId ? <><strong>#{agreement.orderId}</strong><small>Linked order</small></> : <span>—</span>}</td>
                          <td><StatusBadge value={agreement.paymentStatus} type="payment" /></td>
                          <td><StatusBadge value={agreement.status} /></td>
                          <td><div style={{display:'flex',gap:6,alignItems:'center'}}><button type="button" onClick={() => openAgreementDetails(agreement)} title="View Agreement"><Eye size={17} /></button><button type="button" onClick={() => printAgreement(agreement)} title="Print Agreement"><Printer size={17} /></button>{['draft','pending_acceptance'].includes(String(agreement.status || '').toLowerCase()) && String(agreement.paymentStatus || 'pending').toLowerCase() !== 'paid' ? <button type="button" onClick={() => deletePendingAgreement(agreement)} title="Delete Pending Agreement"><Trash2 size={17} /></button> : null}</div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </main>
      {showModal && (
        <div className="vcca-modal-backdrop">
          <form className="vcca-modal" onSubmit={handleCreateAgreement}>
            <div className="vcca-modal-heading"><span>AGREEMENT ENTRY</span><h3>Create Agreement</h3><p>Subscription select karein. Agar us subscription ka purchase order hai to backend latest matching order, hardware aur warranty snapshot automatically link karega.</p></div>
            {error && <div className="vcca-error vcca-modal-error">{error}</div>}
            <label>Vendor Subscription<select value={form.subscriptionId} onChange={(event) => setForm((previous) => ({ ...previous, subscriptionId: event.target.value, orderId: '' }))}><option value="">Select Subscription</option>{subscriptions.map((item) => <option key={item.subscriptionId} value={item.subscriptionId}>{item.businessName} | {item.planName} | {money(item.amount)}</option>)}</select></label>
            {selectedSubscription && (
              <div className="vcca-subscription-info">
                <strong>{selectedSubscription.businessName}</strong><span>{selectedSubscription.ownerName || ''}</span>
                <div><span>Plan: <b>{selectedSubscription.planName}</b></span><span>Amount: <b>{money(selectedSubscription.amount)}</b></span></div>
                <div><span>Start: <b>{selectedSubscription.startDate}</b></span><span>Expiry: <b>{selectedSubscription.expiryDate}</b></span></div>
                <div><span>Payments: <b>{matchingPayments.length}</b></span><span>Latest: <b>{matchingPayments[0]?.status || 'pending'}</b></span></div>
              </div>
            )}
            <div className="vcca-form-grid">
              <label>Purchase Order ID <small>(Optional)</small><input type="number" min="1" value={form.orderId} onChange={(event) => setForm((previous) => ({ ...previous, orderId: event.target.value }))} placeholder="Blank = latest matching order auto-link" /></label>
              <label>Agreement Version<input value={form.agreementVersion} onChange={(event) => setForm((previous) => ({ ...previous, agreementVersion: event.target.value }))} placeholder="1.0" /></label>
            </div>
            <div className="vcca-form-grid">
              <label>Agreement Date<input type="date" value={form.agreementDate} onChange={(event) => setForm((previous) => ({ ...previous, agreementDate: event.target.value }))} /></label>
              <label>Order Linking<input value={form.orderId ? `Manual Order #${form.orderId}` : 'Automatic latest matching order'} readOnly /></label>
            </div>
            <label>Custom Terms <small>(Optional)</small><textarea value={form.terms} onChange={(event) => setForm((previous) => ({ ...previous, terms: event.target.value }))} placeholder="Blank chhodne par default VAKRA agreement terms use honge." /></label>
            <label>Notes<textarea value={form.notes} onChange={(event) => setForm((previous) => ({ ...previous, notes: event.target.value }))} placeholder="Optional internal notes" /></label>
            <div className="vcca-modal-actions"><button type="button" onClick={() => { setShowModal(false); setError(''); resetForm(); }}>Cancel</button><button type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Agreement'}</button></div>
          </form>
        </div>
      )}
      {detailsAgreement && (
        <div className="vcca-modal-backdrop">
          <div className="vcca-modal" style={{ maxWidth: 860 }}>
            <div className="vcca-modal-heading"><span>AGREEMENT DETAILS</span><h3>{detailsAgreement.agreementNo}</h3><p>Vendor, purchase order, hardware warranty, pricing aur acceptance snapshot.</p></div>
            <div className="vcca-subscription-info">
              <strong>{detailsAgreement.businessName || snapshot?.vendor?.businessName || 'Vendor'}</strong>
              <span>{detailsAgreement.ownerName || snapshot?.vendor?.ownerName || ''}</span>
              <div><span>Agreement: <b>{detailsAgreement.agreementNo}</b></span><span>Version: <b>{detailsAgreement.agreementVersion || '1.0'}</b></span></div>
              <div><span>Plan: <b>{detailsAgreement.planName || snapshot?.subscription?.planName || '—'}</b></span><span>Order: <b>{detailsAgreement.orderId ? `#${detailsAgreement.orderId}` : '—'}</b></span></div>
              <div><span>Payment: <b>{detailsAgreement.paymentStatus || 'pending'}</b></span><span>Status: <b>{String(detailsAgreement.status || '').replaceAll('_', ' ')}</b></span></div>
            </div>
            {snapshotPurchase && (
              <div className="vcca-subscription-info">
                <strong>Purchase Snapshot</strong>
                <div><span>Type: <b>{String(snapshotPurchase.type || 'subscription').replaceAll('_', ' ')}</b></span><span>Final Amount: <b>{money(snapshotPurchase.finalAmount ?? detailsAgreement.amount)}</b></span></div>
                <div><span>Normal Total: <b>{money(snapshotPurchase.normalTotal ?? detailsAgreement.amount)}</b></span><span>Discount: <b>{money(snapshotPurchase.discountAmount || 0)}</b></span></div>
              </div>
            )}
            {snapshotItems.length > 0 && (
              <div className="vcca-table-wrap">
                <table>
                  <thead><tr><th>Item</th><th>Type</th><th>Qty</th><th>Price</th><th>Warranty</th></tr></thead>
                  <tbody>
                    {snapshotItems.map((item, index) => {
                      const warranty = item.warranty || {};
                      const warrantyEnabled = warranty.enabled === true || Number(item.warrantyEnabled || 0) === 1;
                      const warrantyMonths = warranty.months ?? item.warrantyMonths;
                      const warrantyType = warranty.type || item.warrantyType || '';
                      const warrantyTerms = warranty.terms || item.warrantyTerms || '';
                      return (
                        <tr key={`${item.referenceId || item.id || index}-${index}`}>
                          <td><strong>{item.name || item.itemName || 'Item'}</strong>{warrantyTerms ? <small>{warrantyTerms}</small> : null}</td>
                          <td>{item.itemType || item.type || '—'}</td>
                          <td>{item.quantity || item.qty || 1}</td>
                          <td>{money(item.unitPrice ?? item.price ?? 0)}</td>
                          <td>{warrantyEnabled ? <><strong>{warrantyMonths ? `${warrantyMonths} Months` : 'Included'}</strong><small>{warrantyType || 'Warranty'}</small></> : 'No Warranty'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="vcca-subscription-info">
              <strong>Acceptance</strong>
              <div><span>Accepted By: <b>{detailsAgreement.acceptedByName || 'Not accepted yet'}</b></span><span>Accepted At: <b>{detailsAgreement.acceptedAt || '—'}</b></span></div>
              <div><span>IP: <b>{detailsAgreement.acceptedIp || '—'}</b></span><span>Payment: <b>{detailsAgreement.paymentStatus || 'pending'}</b></span></div>
            </div>
            <label>Agreement Terms<textarea value={detailsAgreement.terms || ''} readOnly style={{ minHeight: 220 }} /></label>
            {detailsAgreement.notes ? <label>Notes<textarea value={detailsAgreement.notes} readOnly /></label> : null}
            <div className="vcca-modal-actions">{['draft','pending_acceptance'].includes(String(detailsAgreement.status || '').toLowerCase()) && String(detailsAgreement.paymentStatus || 'pending').toLowerCase() !== 'paid' ? <button type="button" onClick={() => deletePendingAgreement(detailsAgreement)}><Trash2 size={17} /> Delete Pending</button> : null}<button type="button" onClick={() => printAgreement(detailsAgreement)}><Printer size={17} /> Print Agreement</button><button type="button" onClick={() => setDetailsAgreement(null)}><X size={17} /> Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ControlCenterAgreements;
